import type React from "react";
import type { ReactElement, ReactNode, Ref } from "react";
// eslint-disable-next-line no-restricted-imports
import { Combobox as ComboboxBase } from "@base-ui/react/combobox";
// eslint-disable-next-line no-restricted-imports
import { Select as SelectBase } from "@base-ui/react/select";
import classNames from "classnames";
import { unsupportedPickerKind, usePickerRoot } from "./PickerContext";

/** The subset of Base UI trigger state every root kind exposes. */
export interface PickerTriggerState {
    open: boolean;
    disabled: boolean;
}

/** Props Base UI hands to a custom trigger element. */
export type PickerTriggerRenderProps =
    React.HTMLAttributes<HTMLButtonElement> & {
        ref?: Ref<HTMLButtonElement>;
    };

export interface PickerTriggerProps extends Omit<
    React.ComponentPropsWithRef<"button">,
    "className" | "style" | "children"
> {
    children?: ReactNode;
    className?: string;
    nativeButton?: boolean;
    render?:
        | ReactElement
        | ((
              props: PickerTriggerRenderProps,
              state: PickerTriggerState,
          ) => ReactElement);
}

/**
 * The active root kind's Base UI trigger. Base UI merges the consumer `ref`
 * and event handlers with its own, so a `render` element keeps both.
 */
export const PickerTrigger = ({ className, ...props }: PickerTriggerProps) => {
    const root = usePickerRoot();
    const triggerClassName = classNames(root.classNames.trigger, className);

    switch (root.kind) {
        case "combobox":
            return (
                <ComboboxBase.Trigger
                    className={triggerClassName}
                    data-picker-trigger={root.chainId}
                    {...props}
                />
            );
        case "select":
            return (
                <SelectBase.Trigger
                    className={triggerClassName}
                    data-picker-trigger={root.chainId}
                    {...props}
                />
            );
        default:
            return unsupportedPickerKind(root.kind);
    }
};
